import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';

interface QuizQuestion {
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}

const quizQuestions: QuizQuestion[] = [
  {
    question: "According to the Pinggang Pinoy model, what proportion of your plate should be filled with fruits and vegetables?",
    options: [
      "One quarter (¼)",
      "One half (½)",
      "Three quarters (¾)",
      "None"
    ],
    correctAnswer: 1,
    explanation: "Following the Pinggang Pinoy model, half your plate should be filled with fruits and vegetables (Glow Foods). This ensures you get adequate fiber, vitamins, and minerals."
  },
  {
    question: "What category of food is found in the one-quarter (¼) portion alongside protein foods?",
    options: [
      "Glow Foods",
      "Rice or Energy Foods",
      "Water",
      "Sweets"
    ],
    correctAnswer: 1,
    explanation: "One quarter of your plate should be dedicated to Rice or Energy Foods (Go Foods). This helps maintain balanced blood sugar levels while providing necessary energy."
  },
  {
    question: "Which of these is considered the ideal drink choice with meals according to this guide?",
    options: [
      "Sugary juice",
      "Soda",
      "Water or milk",
      "Coffee"
    ],
    correctAnswer: 2,
    explanation: "Water or milk are the ideal beverages to accompany your meals. They support hydration and nutrition without adding unnecessary sugars or caffeine."
  },
  {
    question: "The primary purpose of the Pinggang Pinoy guide is to promote:",
    options: [
      "Extreme dieting",
      "Balance, variety, and moderation in meal composition",
      "Eating only imported foods",
      "High consumption of fatty foods"
    ],
    correctAnswer: 1,
    explanation: "The Pinggang Pinoy guide promotes balance, variety, and moderation in meal composition. It is designed to help Filipinos achieve proper nutrition through simple proportions."
  },
  {
    question: "If your plate is mostly rice, which food group are you most likely needing to increase to achieve proper Pinggang Pinoy balance?",
    options: [
      "Energy Foods",
      "Protein",
      "Fruits and Vegetables",
      "Go Foods"
    ],
    correctAnswer: 2,
    explanation: "If your plate is dominated by rice, you need to increase your fruits and vegetables portion to achieve the recommended half-plate proportion of Glow Foods."
  }
];

const NutritionalModule2QuizPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress } = useModuleProgress('nutritionalM2');
  const [currentQuestion, setCurrentQuestion] = useState<number>(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [responses, setResponses] = useState<{[key: number]: number}>({});
  const [showCompletion, setShowCompletion] = useState(false);

  const handleAnswerSelect = (answerIndex: number) => {
    if (responses[currentQuestion] === undefined) {
      setSelectedAnswer(answerIndex);
      setShowExplanation(true);
      setResponses(prev => ({...prev, [currentQuestion]: answerIndex}));
    }
  };

  const handleNext = () => {
    if (currentQuestion < quizQuestions.length - 1) {
      const nextQuestion = currentQuestion + 1;
      setCurrentQuestion(nextQuestion);
      setSelectedAnswer(null);
      setShowExplanation(false);
      saveProgress(nextQuestion + 1);
    } else {
      saveProgress(quizQuestions.length, true);
      setShowCompletion(true);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      const prevQuestion = currentQuestion - 1;
      const previousResponse = responses[prevQuestion];
      setCurrentQuestion(prevQuestion);
      setSelectedAnswer(previousResponse ?? null);
      setShowExplanation(previousResponse !== undefined);
      saveProgress(prevQuestion + 1);
    }
  };

  const handleExit = () => {
    navigate('/panel/learn/nutritional-resources');
  };

  if (showCompletion) {
    return (
      <div className="quiz-completion-section">
        <h2 className="completion-title">
          <span>Congratulations!</span>
        </h2>
        <p className="completion-subtitle">
          You've successfully completed the Module 2 quiz!
        </p>

        <div className="completion-card">
          <div className="achievement-section">
            <h3 className="section-title">
              <span>Key Achievements</span>
            </h3>
            <p className="achievement-intro">You've demonstrated a great understanding of:</p>
            <ul className="achievement-list">
              <li>
                <span className="check"></span>
                <span>The Pinggang Pinoy model proportions</span>
              </li>
              <li>
                <span className="check"></span>
                <span>Balanced meal composition principles</span>
              </li>
              <li>
                <span className="check"></span>
                <span>Food groups and their proper portions</span>
              </li>
            </ul>
          </div>

          <div className="next-steps-section">
            <h3 className="section-title">
              <span>Next Steps</span>
            </h3>
            <p>Apply these portioning guidelines in your daily meals!</p>
            <p>Ready to continue your wellness journey?</p>
          </div>

          <button
            className="resource-button"
            onClick={handleExit}
          >
            Return to Nutritional Resources
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading quiz...</p>
      </div>
    );
  }

  const progress = ((currentQuestion + 1) / quizQuestions.length) * 100;
  const currentQuiz = quizQuestions[currentQuestion];

  return (
    <div className="module-form">
      <div className="module-container">
        <div className="module-left nutritional-panel">
          <div className="module-header">
            <h1>Module 2 Quiz</h1>
            <p className="module-subtitle">Macronutrients and Micronutrients</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Let's review what we've learned about macronutrients and micronutrients.
              There's no scoring - this is just to reinforce the main ideas!
            </p>
          </div>
        </div>

        <div className="assessment-right-panel">
          <div className="assessment-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Question {currentQuestion + 1} of {quizQuestions.length}
              </span>
            </div>

            <div className="question-section">
              <h2 className="question-title">
                {currentQuiz.question}
              </h2>
              <p className="question-instruction">
                Please select the answer that best matches your understanding.
              </p>

              <div className="answer-options">
                {currentQuiz.options.map((option, index) => (
                  <label key={index} className="answer-option">
                    <input
                      type="radio"
                      name={`question-${currentQuestion}`}
                      value={index}
                      checked={selectedAnswer === index}
                      onChange={() => handleAnswerSelect(index)}
                      disabled={selectedAnswer !== null}
                    />
                    <span className="option-text">{option}</span>
                  </label>
                ))}
              </div>

              {showExplanation && (
                <div className="explanation">
                  <h3>{selectedAnswer === currentQuiz.correctAnswer ? 'Correct!' : 'Remember:'}</h3>
                  <p>{currentQuiz.explanation}</p>
                </div>
              )}
            </div>

            <div className="navigation-buttons">
              <button
                className="btn-secondary"
                onClick={currentQuestion === 0 ? handleExit : handleBack}
              >
                {currentQuestion === 0 ? 'Exit Quiz' : 'Previous Question'}
              </button>
              
              {currentQuestion < quizQuestions.length - 1 ? (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={selectedAnswer === null}
                >
                  Next Question
                </button>
              ) : (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={selectedAnswer === null}
                >
                  Complete Quiz
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NutritionalModule2QuizPage;

